﻿import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { prepareSearchCards, updateSearchCount } from '@theme/rudo-search';

/** One owner for pagination. No speculative requests or poisoned retry state. */
export default class PaginatedList extends Component {
  #controller;
  #listeners;
  #generation = 0;
  #armed = false;
  #lastScroll = 0;
  #observer;

  get sectionId() { return this.getAttribute('section-id'); }
  get grid() { return this.querySelector('[ref="grid"]'); }

  connectedCallback() {
    super.connectedCallback();
    const rawQuery = this.dataset.rudoSearchQuery || '';
    const exact = /^\(title:"(.+)"\) OR \(sku:"\1"\)$/.exec(rawQuery);
    if (exact) {
      this.dataset.rudoExactQuery = rawQuery;
      this.dataset.rudoSearchQuery = exact[1];
    }
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#lastScroll = window.scrollY;
    this.prepare();
    this.#recoverSearch();
    this.addEventListener('click', this.#click, { signal });
    window.addEventListener('scroll', this.#scroll, { passive: true, signal });
    document.addEventListener(ThemeEvents.FilterUpdate, this.cancelPagination, { signal });
    document.addEventListener('rudo:section-rendered', this.#rendered, { signal });
    if ('IntersectionObserver' in window && this.getAttribute('infinite-scroll') !== 'false') {
      this.#observer = new IntersectionObserver(entries => {
        if (this.#armed && entries.some(e => e.isIntersecting)) this.loadMore();
      }, { rootMargin: '200px' });
      const next = this.querySelector('[ref="viewMoreNext"]');
      if (next) this.#observer.observe(next);
    }
    this.updateLinks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cancelPagination();
    this.#listeners?.abort();
    this.#observer?.disconnect();
  }

  prepare() {
    if (this.hasAttribute('data-rudo-search-query')) {
      prepareSearchCards(this, this.dataset.rudoSearchQuery);
      updateSearchCount(this);
    }
  }

  async #recoverSearch() {
    if (!this.hasAttribute('data-rudo-search-query') || !this.querySelector('[data-rudo-load-more]') || this.querySelector('[data-rudo-search-text]:not([hidden])')) return;
    const term = (this.dataset.rudoSearchQuery || '').trim();
    if (term.length < 2 || term.length > 80 || /[:()]/.test(term)) return;
    // Shopify may fill a price-sorted page with matches from descriptions.
    // Ask Shopify for name/SKU matches once, without walking eight broad pages.
    const exact = `(title:${JSON.stringify(term)}) OR (sku:${JSON.stringify(term)})`;
    const url = new URL(location.href);
    url.searchParams.set('q', exact);
    const controller = new AbortController();
    this.#controller = controller;
    const generation = this.#generation;
    this.status('Buscando coincidencias en nombre y código…', 'loading');
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url, controller.signal);
      if (!this.isConnected || controller.signal.aborted || generation !== this.#generation) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('results-list');
      if (!source?.querySelector('[ref="grid"]')) throw new Error('search_recovery_invalid');
      this.dataset.rudoExactQuery = exact;
      this.replaceChildren(...source.childNodes);
      this.prepare();
      this.#observer?.disconnect();
      const sentinel = this.querySelector('[ref="viewMoreNext"]');
      if (sentinel) this.#observer?.observe(sentinel);
      this.updateLinks();
      this.status(this.querySelector('[data-rudo-search-text]:not([hidden])') ? 'Coincidencias cargadas.' : 'No hay coincidencias visibles. Prueba otra búsqueda o carga más.', 'complete');
    } catch (error) {
      if (!controller.signal.aborted && generation === this.#generation) this.status('No se pudo completar la búsqueda. Prueba cargar más.', 'error');
    } finally {
      if (this.#controller === controller) this.#controller = null;
    }
  }

  cancelPagination = () => {
    this.#generation++;
    this.#controller?.abort();
    this.#controller = null;
    this.#armed = false;
    this.setAttribute('aria-busy', 'false');
  };

  #rendered = (event) => {
    if (event.detail.sectionId.replace('shopify-section-', '') !== this.sectionId) return;
    this.cancelPagination();
    this.prepare();
    this.updateLinks();
  };

  #scroll = () => {
    const y = window.scrollY;
    const down = y > this.#lastScroll + 2;
    this.#lastScroll = y;
    if (!down || this.#controller || this.dataset.paginationState === 'error') return;
    this.#armed = true;
    const sentinel = this.querySelector('[ref="viewMoreNext"]');
    if (this.getAttribute('infinite-scroll') !== 'false' && sentinel && sentinel.getBoundingClientRect().top < innerHeight + 200) this.loadMore();
  };

  #click = (event) => {
    const link = event.target.closest('[data-rudo-load-more], [data-rudo-load-previous]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0) return;
    event.preventDefault();
    this.loadMore(link.hasAttribute('data-rudo-load-previous') ? 'previous' : 'next');
  };

  page(direction) {
    const cards = [...(this.grid?.querySelectorAll(':scope > [data-page]') || [])];
    const current = Number((direction === 'previous' ? cards[0] : cards.at(-1))?.dataset.page || this.grid?.dataset.currentPage || 1);
    const page = current + (direction === 'previous' ? -1 : 1);
    if (page < 1 || page > Number(this.grid?.dataset.lastPage || 1)) return null;
    const url = new URL(location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.delete('section_id');
    return { page, url };
  }

  updateLinks() {
    for (const [direction, selector] of [['next', '[data-rudo-load-more]'], ['previous', '[data-rudo-load-previous]']]) {
      const next = this.page(direction);
      const link = this.querySelector(selector);
      if (link) { link.hidden = !next; if (next) link.href = next.url.toString(); }
    }
    if (!this.page('next')) this.status('Fin de productos.', 'end');
  }

  status(message, state) {
    this.dataset.paginationState = state;
    this.setAttribute('aria-busy', String(state === 'loading'));
    const status = this.querySelector('[data-rudo-pagination-status]');
    if (status) status.textContent = message;
    const link = this.querySelector('[data-rudo-load-more]');
    if (link) link.textContent = state === 'error' ? 'Reintentar carga' : 'Cargar más';
  }

  async loadMore(direction = 'next') {
    if (this.#controller) return;
    const next = this.page(direction);
    if (!next) return;
    const controller = new AbortController();
    this.#controller = controller;
    this.#armed = false;
    const generation = this.#generation;
    this.status('Cargando más productos…', 'loading');
    try {
      const requestUrl = new URL(next.url);
      if (this.dataset.rudoExactQuery) requestUrl.searchParams.set('q', this.dataset.rudoExactQuery);
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, requestUrl, controller.signal);
      if (!this.isConnected || controller.signal.aborted || generation !== this.#generation) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('[ref="grid"]');
      if (!source) throw new Error('pagination_invalid_grid');
      const cards = [...source.querySelectorAll(':scope > [ref="cards[]"]')];
      if (cards.some(card => Number(card.dataset.page) !== next.page)) throw new Error('pagination_wrong_page');
      if (!cards.length && next.page <= Number(source.dataset.lastPage)) throw new Error('pagination_empty_response');
      if (this.hasAttribute('data-rudo-search-query')) prepareSearchCards(source, this.dataset.rudoSearchQuery);
      const known = new Set([...this.grid.querySelectorAll(':scope > [ref="cards[]"]')].map(c => c.dataset.productId || c.getAttribute('href') || c.id));
      const newCards = cards.filter(c => !known.has(c.dataset.productId || c.getAttribute('href') || c.id));
      const anchor = this.grid.firstElementChild;
      const oldTop = anchor?.getBoundingClientRect().top;
      if (direction === 'previous') this.grid.prepend(...newCards); else this.grid.append(...newCards);
      this.grid.dataset.currentPage = String(next.page);
      this.grid.dataset.lastPage = source.dataset.lastPage;
      history.replaceState({ ...history.state, rudoPage: next.page }, '', next.url);
      if (direction === 'previous' && anchor && oldTop != null) window.scrollBy(0, anchor.getBoundingClientRect().top - oldTop);
      updateSearchCount(this);
      this.status('Más productos cargados.', 'complete');
      this.updateLinks();
    } catch (error) {
      if (!controller.signal.aborted && generation === this.#generation) this.status('No se pudo cargar. Revisa tu conexión y vuelve a intentar.', 'error');
    } finally {
      if (this.#controller === controller) this.#controller = null;
    }
  }
}

